import { Card, CardBody } from 'react-bootstrap';
import { t } from 'react-switch-lang';
import Image from 'next/image';
import Decimal from 'decimal.js';
import Button from '../Button';
import styles from '../../styles/PaymentOption.module.scss';
import { events } from '../../utils/Amplitude';
import { formatAsTestId, getText } from '../../utils/Helpers';
import ReplaceTextWithElement from '../ReplaceTextWithElement';
import { FormatAmount } from '../../utils/Validations';

function formatPercentage(number) {
  if (!Decimal.isDecimal(number)) return null;
  const percentage = number.mul(100);
  if (percentage.mod(1).isZero()) {
    return `${percentage.toFixed(1)}%`;
  }
  return `${percentage}%`;
}

/**
 * @param {import('../../context/PaymentProcessContext').FeeV2} fee
 * @param {number} amount
 */

export const calculateFee = (fee, amount) => {
  if (!fee || Number.isNaN(amount)) return null;

  const tiers = (fee.Tiers || []).slice().sort((a, b) => a.Amount - b.Amount);
  if (tiers.length === 0) return null;

  const limit = new Decimal(fee.AmountLimit || 0);
  const amt = new Decimal(amount);

  let totalFixed = new Decimal(0);
  let totalPercent = new Decimal(0);

  const reversedTiers = tiers.slice().reverse();

  if (amt.lte(limit) || reversedTiers.length === 1) { // Within first tier or one tier
    const applicableTier = reversedTiers.find((tier) => amt.gte(new Decimal(tier.Amount)));
    if (!applicableTier) return null;

    totalFixed = new Decimal(applicableTier.Fixed || 0);
    totalPercent = new Decimal(applicableTier.Percent || 0);
  } else { // Above tier limit
    const fullTiers = amt.div(limit).floor();
    const remainder = amt.mod(limit); // remaining amount after full blocks
    const topTier = reversedTiers[0]; // highest tier

    // Add fees for all full tier blocks
    totalFixed = totalFixed.add(fullTiers.mul(topTier.Fixed || 0));
    totalPercent = totalPercent.add(fullTiers.mul(topTier.Percent || 0));

    // If there’s a remainder, apply the correct tier for that leftover amount
    if (remainder.gt(0)) {
      const applicableTier = reversedTiers.find((tier) => remainder.gte(new Decimal(tier.Amount)));
      if (applicableTier) {
        totalFixed = totalFixed.add(new Decimal(applicableTier.Fixed || 0));
        totalPercent = totalPercent.add(new Decimal(applicableTier.Percent || 0));
      }
    }
  }

  const totalFee = amt.mul(totalPercent).add(totalFixed).toDecimalPlaces(2).toNumber();

  let feeString = FormatAmount(totalFee);
  if (totalPercent.gt(0) && totalFixed.gt(0)) {
    feeString = `${feeString} (${formatPercentage(totalPercent)} + ${FormatAmount(totalFixed.toNumber())})`;
  } else if (totalPercent.gt(0)) {
    feeString = `${feeString} (${formatPercentage(totalPercent)})`;
  }

  return { totalFee, feeString };
};

const poImagesSrc = {
  Credit: {
    height: 32,
    images: [
      {
        path: '/images/logos/CreditCards/Visa.png',
        alt: 'Visa_ImageDescription',
      },
      {
        path: '/images/logos/CreditCards/Mastercard.png',
        alt: 'Mastercard_ImageDescription',
      },
      {
        path: '/images/logos/CreditCards/Amex.png',
        alt: 'Amex_ImageDescription',
      },
      {
        path: '/images/logos/CreditCards/UnionPay.png',
        alt: 'UnionPay_ImageDescription',
      },
    ],
  },
  InPerson: {
    height: 32,
    images: [
      {
        path: '/images/logos/CanadaPost.png',
        alt: 'AvailableAtCanadaPostLogo_ImageDescription',
      },
    ],
  },
  eTransfer: {
    height: 34,
    images: [
      {
        path: '/images/logos/eTransfer.png',
        alt: 'ETransfer_ImageDescription',
      },
    ],
  },
  PayPal: {
    height: 30,
    images: [
      {
        path: '/images/logos/PayPal.png',
        alt: 'PayPalLogo_ImageDescription',
      },
    ],
  },
  PeoplesRFM: {
    height: 34,
    images: [
      {
        path: '/images/logos/eTransfer.png',
        alt: 'ETransfer_ImageDescription',
      },
    ],
  },
};

export function PaymentOptionImages({ paymentOptionName, height, gap }) {
  const imgObj = poImagesSrc[paymentOptionName];
  return (
    imgObj?.images?.map(({ path, alt }) => (
      <Image
        src={path}
        key={path}
        alt={t(alt)}
        style={{ objectFit: 'contain', width: 'auto', height: height || imgObj.height, marginInline: gap || '3px' }}
        sizes="(max-width: 768px) 100vw, 50vw"
        height={0}
        width={0}
      />
    ))
  );
}

/**
 * @param {object} props
 * @param {import('../../context/PaymentProcessContext').PaymentOption} props.paymentOptionInfo
 */
export default function PaymentOptionCard({
  paymentOptionInfo,
  paymentAmount,
  onSelect,
}) {
  const {
    Name,
    Title,
    Description1,
    Description2,
    Delivery,
    FeeV2,
    Blurb2,
    Enabled,
  } = paymentOptionInfo;
  const amount = Number(paymentAmount);
  const calculatedFee = calculateFee(FeeV2, amount);

  // If the calculateFee return null, that means the tiers for FeeV2 are not configured properly,
  // in that case, not showing the payment option at all.
  if (!calculatedFee) return null;
  return (
    <Button
      className={styles.poCardButton}
      ampEvent={events.USER_SELECTED_PAYMENT_OPTION}
      ampEventProps={{ PaymentOption: Name }}
      onClick={() => onSelect(paymentOptionInfo, calculatedFee.totalFee)}
      ariaLabel={t('PaymentOption_ButtonDescription').replace('xPaymentOption', getText(Title))}
      disabled={!Enabled}
      role="tab"
      testId={formatAsTestId(`payment option ${Name}`, 'button')}
    >
      <Card className={`${styles.poContainer} ${!Enabled && styles.poContainerDisabled}`}>
        <CardBody className={styles.poContent}>
          <div className={styles.poData}>
            <h2>
              <ReplaceTextWithElement
                text={getText(Title)}
                replaceWith={<i>Interac</i>}
                replaceKey="Interac"
              />
            </h2>
            <div className={styles.poDescriptions}>
              {!Enabled ?
                <p>{getText(Blurb2)}</p> : (
                  <>
                    <p>{getText(Description1)}</p>
                    <p>{getText(Description2)}</p>
                  </>
                )}
            </div>
            <h3 className={styles.poProcessingTime}>
              <b>{`${t('PaymentOption_ProcessingTime_Title')}: `}</b>
              {getText(Delivery)}
            </h3>
            <h3 className={styles.poFee}>
              <b>{`${t('PaymentOption_Fee_Title')}: `}</b>
              {calculatedFee?.feeString}
            </h3>
          </div>
          <div className={styles.poImagesContainer}>
            <PaymentOptionImages paymentOptionName={Name} />
          </div>
        </CardBody>
      </Card>
    </Button>
  );
}
